import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, switchMap, throwError, Observable } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export function authInterceptor(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
) {
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and trigger the token refresh.
    const accessToken = authService.accessToken;
    if (accessToken) {
        // Check if the token is expired
        const isTokenExpired = AuthUtils.isTokenExpired(accessToken);

        if (!isTokenExpired) {
            newReq = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
            });
        }
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // Don't try to refresh if this is already a refresh request
                if (req.url.includes('/auth/refresh')) {
                    authService.signOut();
                    return throwError(() => error);
                }

                // Check if the error is due to token expiration
                const refreshToken = authService.refreshToken;
                if (refreshToken) {
                    // Try to refresh the token
                    return authService.refreshAccessToken().pipe(
                        switchMap((response) => {
                            // Token refreshed successfully, retry the original request
                            const retryReq = req.clone({
                                headers: req.headers.set('Authorization', `Bearer ${authService.accessToken}`),
                            });
                            return next(retryReq);
                        }),
                        catchError((refreshError) => {
                            // Refresh failed, sign out is already handled in refreshAccessToken
                            return throwError(() => error);
                        })
                    );
                }
                
                // No refresh token available, sign out
                authService.signOut();        
            }

            return throwError(() => error);
        })
    );
}
